import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import Text from "new_components/atoms/Text";
import TextInput from "new_components/molecules/TextInput";
import colors from "styles/colors";
import typography from "styles/typography";

const CategoryOptionsEditor = ({
  categoryObj,
  options,               // The current 'options' array for this property
  onChange,              // Called when we want to update that array
  addOptionToCategory,   // Function that adds a new option to the master category
  removeOptionFromCategory, // Function that removes an option from the master category
}) => {
  // Always declare Hooks at the top level of your component
  const [newOption, setNewOption] = useState("");

  // Check if this component has the "Other (specify own)" option toggled
  const hasOtherOption = options?.includes("Other (specify own)");

  // Effect to ensure "Other (specify own)" is kept when the user toggles it in PropertyEditor
  useEffect(() => {
    // If the toggle was turned ON but the option isn't in our list
    if (!hasOtherOption && categoryObj.hasOtherOption) {
      // Add the "Other (specify own)" option to the list
      const updatedOptions = [...(options || []), "Other (specify own)"];
      onChange(updatedOptions);
      
      // Also add to master category if it's not already there
      if (!categoryObj.options.includes("Other (specify own)")) {
        addOptionToCategory(categoryObj.name, "Other (specify own)");
      }
    }
    // If the toggle was turned OFF but the option is still in our list
    else if (hasOtherOption && !categoryObj.hasOtherOption) {
      // Remove the "Other (specify own)" option from the list
      const updatedOptions = options.filter(opt => opt !== "Other (specify own)");
      onChange(updatedOptions);
      
      // Also remove from master category
      if (categoryObj.options.includes("Other (specify own)")) {
        removeOptionFromCategory(categoryObj.name, "Other (specify own)");
      }
    }
  }, [categoryObj.hasOtherOption, hasOtherOption, options, onChange, addOptionToCategory, removeOptionFromCategory, categoryObj.name, categoryObj.options]);

  const handleAddOption = useCallback(() => {
    const trimmed = newOption.trim();
    if (!trimmed) return;

    // 1) Update the master category in the parent
    addOptionToCategory(categoryObj.name, trimmed);

    // 2) Update this property's options array
    onChange([...(options || []), trimmed]);

    // 3) Clear input
    setNewOption("");
  }, [newOption, categoryObj.name, options, addOptionToCategory, onChange]);

  // Handle removing an option
  const handleRemoveOption = useCallback((optionIndex, optionValue) => {
    // If this is the "Other (specify own)" option and it's toggled on through properties,
    // we should toggle off the property instead of just removing the option
    if (optionValue === "Other (specify own)" && categoryObj.hasOtherOption) {
      // This will trigger a re-render and the useEffect will handle the removal
      // We're not directly manipulating properties here, just notifying the parent component
      const updatedOptions = options.filter(opt => opt !== "Other (specify own)");
      onChange(updatedOptions);
      return;
    }
  
    // 1) Create a copy of the current options and remove the item
    const updatedOptions = [...options];
    updatedOptions.splice(optionIndex, 1);
    
    // 2) Update the component's options
    onChange(updatedOptions);
    
    // 3) Also remove from the master category if it exists there
    // Only remove from category if it exists in the category options
    // This prevents unnecessary updates
    if (categoryObj.options.includes(optionValue)) {
      removeOptionFromCategory(categoryObj.name, optionValue);
    }
  }, [options, categoryObj, onChange, removeOptionFromCategory]);

  // If this is a date type category, render different UI but after hook declarations
  if (categoryObj.type === "date") {
    return (
      <Wrapper>
        <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
          Date Selection Category: "{categoryObj.name}"
        </Text>
        <Text typographyStyle={typography.body.regular} color={colors.text.gray} style={{ marginTop: '8px' }}>
          This is a date selection category. Users will be able to select dates from a calendar.
        </Text>
      </Wrapper>
    );
  }

  // For text type categories, render the options UI
  return (
    <Wrapper>
      <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
        Options for category "{categoryObj.name}"
      </Text>

      {/* Show the current component's options as a list with delete buttons */}
      <OptionsList>
        {options && options.length > 0 ? (
          options.map((opt, index) => {
            // Check if this is the "Other" option with toggle on
            const isSpecialOption = opt === "Other (specify own)" && categoryObj.hasOtherOption;
            
            return (
              <OptionItem 
                key={index} 
                isSpecialOption={isSpecialOption}
              >
                <Text typographyStyle={typography.body.regular} color={colors.text.dark}>
                  {opt}
                  {isSpecialOption && (
                    <SpecialOptionLabel>
                      (Can only be removed by turning off the toggle)
                    </SpecialOptionLabel>
                  )}
                </Text>
                {/* Only show the remove button if it's not the special Other option or if the toggle is off */}
                {!isSpecialOption && (
                  <RemoveButton
                    onClick={() => handleRemoveOption(index, opt)}
                    aria-label="Remove option"
                  >
                    <Text typographyStyle={typography.smallText.semibold} color={colors.background.white}>
                      ✕
                    </Text>
                  </RemoveButton>
                )}
              </OptionItem>
            );
          })
        ) : (
          <Text typographyStyle={typography.body.regular} color={colors.text.gray}>
            No options selected
          </Text>
        )}
      </OptionsList>

      {/* Add new option */}
      <AddOptionWrapper>
        {/**
         * IMPORTANT: if your TextInput already calls onChange={props => onChange(props.value)},
         * then here we must do onChange={(value) => setNewOption(value)}, not onChange={(e) => ...}
         */}
        <TextInput
          value={newOption}
          onChange={(value) => setNewOption(value)}
          placeholder="Add new option"
          typographyStyle={typography.body.regular}
        />
        <AddButton onClick={handleAddOption}>
          <Text typographyStyle={typography.smallText.semibold} color={colors.background.white}>
            Add
          </Text>
        </AddButton>
      </AddOptionWrapper>
    </Wrapper>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(CategoryOptionsEditor);

/* --- Styled stuff --- */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  background-color: ${props => props.isSpecialOption ? colors.neutral[100] : colors.background.light};
  ${props => props.isSpecialOption && `
    border: 1px dashed ${colors.neutral[300]};
  `}
`;

const AddOptionWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const AddButton = styled.button`
  background: ${colors.primary[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  &:hover {
    background: ${colors.primary[600]};
  }
`;

const RemoveButton = styled.button`
  background: ${colors.auxiliary.red[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  &:hover {
    background: ${colors.auxiliary.red[600]};
  }
`;

const SpecialOptionLabel = styled.span`
  font-size: 12px;
  font-style: italic;
  color: ${colors.text.gray};
  margin-left: 8px;
`;
